/**
 * Procedural toon creatures (+field) for mobs without a KayKit model: meadow slimes (and the
 * flowering bloom slime), tusked boars, bramble wolves, barrow crawlers and barrow wisps.
 * Each is one SkinnedMesh (one draw + one outline hull) built from primitives with baked vertex
 * colours on a tiny bone rig (body, head, legs, tail), animated procedurally from the same
 * PoseParams the KayKit mobs use (run cycle, attack lunge, hit flinch, stun, death).
 * Geometry is in model space facing +Z (like the KayKit rigs), origin between the feet.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { PoseParams } from './anim';
import { paint, xf } from './outdoor/procs';

export type CreatureKind = 'slime' | 'bloom' | 'boar' | 'wolf' | 'crawler' | 'wisp' | 'fox' | 'stag'; // +ranger-specs: fox / stag (the Beastbound's spirit beast forms)

/** +ranger-specs: the four-legged hunters that share the wolf's rig and gait. */
const LUPINE: ReadonlySet<CreatureKind> = new Set(['wolf', 'fox', 'stag']);

/** Bone slots (not all kinds use all of them). */
export const CB = { root: 0, body: 1, head: 2, fl: 3, fr: 4, bl: 5, br: 6, tail: 7, ml: 8, mr: 9 } as const;
const NB = 10;

export interface CreatureRig {
  kind: CreatureKind;
  geometry: THREE.BufferGeometry;
  bones: THREE.Bone[];
  /** Rest positions of the bones (local). */
  rest: THREE.Vector3[];
  height: number;
  /** Wisps glow and float. */
  glow: boolean;
}

type Part = { g: THREE.BufferGeometry; bone: number };

const tmp = new THREE.Color();
const shade = (hex: number | string | readonly [number, number, number], k: number): [number, number, number] => {
  if (Array.isArray(hex)) return [hex[0] * k, hex[1] * k, hex[2] * k];
  tmp.set(hex as THREE.ColorRepresentation);
  return [tmp.r * k, tmp.g * k, tmp.b * k];
};

function ellipsoid(rx: number, ry: number, rz: number, ws = 12, hs = 8): THREE.BufferGeometry {
  return new THREE.SphereGeometry(1, ws, hs).scale(rx, ry, rz);
}

/** Legs: a tapered cylinder hanging from its hip (origin at the hip). */
function leg(len: number, r: number): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(r, r * 0.7, len, 6).translate(0, -len / 2, 0);
}

function eyes(parts: Part[], bone: number, x: number, y: number, z: number, r: number): void {
  for (const s of [-1, 1]) {
    parts.push({ g: paint(xf(new THREE.SphereGeometry(r, 8, 6), [s * x, y, z]), 0x14141c, [1, 1]), bone });
    parts.push({ g: paint(xf(new THREE.SphereGeometry(r * 0.38, 6, 4), [s * x + r * 0.25, y + r * 0.35, z + r * 0.7]), 0xffffff, [1.2, 1.2]), bone });
  }
}

/** `spirit` (+ranger-specs): a translucent-looking spirit creature that glows in its own tint. */
export function buildCreature(kind: CreatureKind, tintHex: string, spirit = false): CreatureRig {
  const parts: Part[] = [];
  const pivots: THREE.Vector3[] = Array.from({ length: NB }, () => new THREE.Vector3());
  const parent: number[] = [-1, CB.root, CB.body, CB.body, CB.body, CB.body, CB.body, CB.body, CB.body, CB.body];
  const base = shade(tintHex, 1);
  let height = 1.2;
  let glow = spirit;

  if (kind === 'slime' || kind === 'bloom') {
    // A squishy gumdrop: flat bottom, glossy highlight, big friendly eyes.
    pivots[CB.body]!.set(0, 0, 0);
    pivots[CB.head]!.set(0, 0.55, 0);
    const body = new THREE.SphereGeometry(0.78, 18, 12);
    const p = body.attributes['position'] as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      let y = p.getY(i);
      if (y < -0.3) y = -0.3 + (y + 0.3) * 0.2;
      const k = 1 + 0.12 * Math.max(0, -y);
      p.setXYZ(i, p.getX(i) * k, y, p.getZ(i) * k);
    }
    body.computeVertexNormals();
    body.translate(0, 0.33, 0).scale(1, 1.05, 1);
    parts.push({ g: paint(body, base, [0.62, 1.2]), bone: CB.body });
    parts.push({ g: paint(xf(ellipsoid(0.2, 0.09, 0.14, 8, 5), [-0.22, 1.02, 0.28], [0.3, 0, 0.3]), shade(base, 1.9), [1, 1]), bone: CB.head });
    eyes(parts, CB.head, 0.22, 0.12, 0.6, 0.11);
    // Little mouth.
    parts.push({ g: paint(xf(new THREE.TorusGeometry(0.07, 0.022, 4, 8, Math.PI), [0, -0.06, 0.7], [0, 0, Math.PI]), 0x2a1a20, [1, 1]), bone: CB.head });
    if (kind === 'bloom') {
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        parts.push({ g: paint(xf(ellipsoid(0.17, 0.05, 0.1, 8, 4), [Math.cos(a) * 0.17, 0.5, Math.sin(a) * 0.17], [0, -a, 0.35]), 0xf6a0c8, [0.95, 1.15]), bone: CB.head });
      }
      parts.push({ g: paint(xf(new THREE.SphereGeometry(0.1, 8, 6), [0, 0.53, 0]), 0xffd84a, [1, 1.2]), bone: CB.head });
      parts.push({ g: paint(xf(new THREE.CylinderGeometry(0.03, 0.03, 0.22, 5), [0, 0.4, 0]), 0x4f9a3a, [1, 1]), bone: CB.head });
    } else {
      // A leaf sprout.
      parts.push({ g: paint(xf(ellipsoid(0.14, 0.04, 0.07, 8, 4), [0.08, 0.52, 0], [0, 0, 0.5]), 0x5fb84a, [0.9, 1.1]), bone: CB.head });
      parts.push({ g: paint(xf(new THREE.CylinderGeometry(0.025, 0.025, 0.18, 5), [0, 0.42, 0]), 0x4f9a3a, [1, 1]), bone: CB.head });
    }
    height = 1.25;
  } else if (kind === 'boar') {
    pivots[CB.body]!.set(0, 0.85, 0);
    pivots[CB.head]!.set(0, 0.95, 0.85);
    pivots[CB.fl]!.set(0.3, 0.62, 0.55);
    pivots[CB.fr]!.set(-0.3, 0.62, 0.55);
    pivots[CB.bl]!.set(0.3, 0.62, -0.55);
    pivots[CB.br]!.set(-0.3, 0.62, -0.55);
    pivots[CB.tail]!.set(0, 1.0, -1.0);
    parts.push({ g: paint(xf(ellipsoid(0.55, 0.52, 0.95), [0, 0.88, -0.05]), base, [0.6, 1.1]), bone: CB.body });
    parts.push({ g: paint(xf(ellipsoid(0.5, 0.45, 0.5), [0, 0.92, 0.45]), shade(base, 0.85), [0.6, 1.05]), bone: CB.body });
    // Bristle ridge along the back.
    for (let i = 0; i < 7; i++) parts.push({ g: paint(xf(new THREE.ConeGeometry(0.09, 0.3, 4), [0, 1.36 - Math.abs(i - 3) * 0.03, 0.55 - i * 0.2], [-0.4, 0, 0]), shade(base, 0.45), [0.8, 1]), bone: CB.body });
    // Head: a wedge with a pink snout, tusks, ears.
    parts.push({ g: paint(xf(ellipsoid(0.36, 0.34, 0.42), [0, 0.92, 1.05], [0.25, 0, 0]), shade(base, 0.95), [0.65, 1.08]), bone: CB.head });
    parts.push({ g: paint(xf(new THREE.CylinderGeometry(0.17, 0.2, 0.2, 10).rotateX(Math.PI / 2), [0, 0.8, 1.45]), 0xd88a86, [0.9, 1.05]), bone: CB.head });
    parts.push({ g: paint(xf(new THREE.CylinderGeometry(0.15, 0.15, 0.02, 10).rotateX(Math.PI / 2), [0, 0.8, 1.56]), 0x6a3a3a, [1, 1]), bone: CB.head });
    for (const s of [-1, 1]) {
      parts.push({ g: paint(xf(new THREE.ConeGeometry(0.05, 0.36, 6), [s * 0.2, 0.86, 1.4], [-0.6, 0, s * 0.5]), 0xf2ead8, [0.9, 1.1]), bone: CB.head });
      parts.push({ g: paint(xf(new THREE.ConeGeometry(0.1, 0.26, 4), [s * 0.22, 1.22, 0.92], [-0.3, 0, -s * 0.5]), shade(base, 0.7), [0.8, 1]), bone: CB.head });
    }
    eyes(parts, CB.head, 0.18, 1.02, 1.32, 0.055);
    for (const b of [CB.fl, CB.fr, CB.bl, CB.br]) {
      const pv = pivots[b]!;
      parts.push({ g: paint(xf(leg(0.62, 0.13), [pv.x, pv.y, pv.z]), shade(base, 0.7), [0.4, 1]), bone: b });
    }
    parts.push({ g: paint(xf(new THREE.ConeGeometry(0.05, 0.35, 5), [0, 1.0, -1.08], [-2.2, 0, 0]), shade(base, 0.6), [1, 1]), bone: CB.tail });
    height = 1.45;
  } else if (kind === 'fox' || kind === 'stag') {
    height = buildBeast(kind, parts, pivots, base);
  } else if (kind === 'wolf') {
    pivots[CB.body]!.set(0, 1.0, 0);
    pivots[CB.head]!.set(0, 1.25, 0.9);
    pivots[CB.fl]!.set(0.24, 0.85, 0.6);
    pivots[CB.fr]!.set(-0.24, 0.85, 0.6);
    pivots[CB.bl]!.set(0.24, 0.85, -0.6);
    pivots[CB.br]!.set(-0.24, 0.85, -0.6);
    pivots[CB.tail]!.set(0, 1.15, -0.95);
    parts.push({ g: paint(xf(ellipsoid(0.4, 0.42, 0.95), [0, 1.08, -0.05]), base, [0.55, 1.1]), bone: CB.body });
    // Chest ruff.
    parts.push({ g: paint(xf(ellipsoid(0.42, 0.5, 0.42), [0, 1.12, 0.55]), shade(base, 1.18), [0.75, 1.1]), bone: CB.body });
    // Spiky mane.
    for (let i = 0; i < 5; i++) parts.push({ g: paint(xf(new THREE.ConeGeometry(0.1, 0.32, 4), [0, 1.52 - i * 0.02, 0.45 - i * 0.22], [-0.6, 0, 0]), shade(base, 0.6), [0.8, 1]), bone: CB.body });
    parts.push({ g: paint(xf(ellipsoid(0.27, 0.26, 0.3), [0, 1.32, 1.05]), base, [0.65, 1.1]), bone: CB.head });
    parts.push({ g: paint(xf(new THREE.BoxGeometry(0.2, 0.17, 0.42), [0, 1.22, 1.38]), shade(base, 1.15), [0.75, 1.05]), bone: CB.head });
    parts.push({ g: paint(xf(new THREE.SphereGeometry(0.06, 6, 4), [0, 1.27, 1.6]), 0x1a1a1a, [1, 1]), bone: CB.head });
    for (const s of [-1, 1]) parts.push({ g: paint(xf(new THREE.ConeGeometry(0.09, 0.28, 4), [s * 0.15, 1.6, 0.98], [-0.15, 0, -s * 0.2]), shade(base, 0.7), [0.8, 1]), bone: CB.head });
    // Glinting amber eyes.
    for (const s of [-1, 1]) parts.push({ g: paint(xf(new THREE.SphereGeometry(0.045, 6, 4), [s * 0.13, 1.38, 1.3]), 0xffc040, [1.3, 1.3]), bone: CB.head });
    for (const b of [CB.fl, CB.fr, CB.bl, CB.br]) {
      const pv = pivots[b]!;
      parts.push({ g: paint(xf(leg(0.85, 0.1), [pv.x, pv.y, pv.z]), shade(base, 0.75), [0.45, 1]), bone: b });
    }
    parts.push({ g: paint(xf(new THREE.ConeGeometry(0.14, 0.75, 6), [0, 1.0, -1.25], [-2.0, 0, 0]), shade(base, 0.9), [0.7, 1.1]), bone: CB.tail });
    height = 1.7;
  } else if (kind === 'crawler') {
    pivots[CB.body]!.set(0, 0.55, 0);
    pivots[CB.head]!.set(0, 0.5, 0.75);
    const legs = [CB.fl, CB.fr, CB.ml, CB.mr, CB.bl, CB.br];
    const zs = [0.45, 0.45, 0, 0, -0.45, -0.45];
    legs.forEach((b, i) => pivots[b]!.set(i % 2 ? -0.45 : 0.45, 0.55, zs[i]!));
    parts.push({ g: paint(xf(ellipsoid(0.62, 0.32, 0.85), [0, 0.6, -0.1]), base, [0.55, 1.1]), bone: CB.body });
    // Bone plates on the back.
    for (let i = 0; i < 4; i++) parts.push({ g: paint(xf(ellipsoid(0.5 - i * 0.06, 0.08, 0.2), [0, 0.88 - i * 0.02, 0.35 - i * 0.32]), shade(base, 1.25), [0.8, 1.1]), bone: CB.body });
    parts.push({ g: paint(xf(ellipsoid(0.3, 0.24, 0.28), [0, 0.55, 0.85]), shade(base, 0.85), [0.6, 1.05]), bone: CB.head });
    for (const s of [-1, 1]) parts.push({ g: paint(xf(new THREE.ConeGeometry(0.05, 0.4, 5), [s * 0.14, 0.45, 1.12], [1.4, 0, -s * 0.5]), 0xe6dcc0, [0.9, 1.1]), bone: CB.head });
    for (const s of [-1, 1]) parts.push({ g: paint(xf(new THREE.SphereGeometry(0.05, 6, 4), [s * 0.12, 0.65, 1.08]), 0x5ef0d8, [1.4, 1.4]), bone: CB.head });
    legs.forEach((b, i) => {
      const pv = pivots[b]!;
      const s = i % 2 ? -1 : 1;
      // Upper leg up and out, lower leg down to the ground.
      const up = new THREE.CylinderGeometry(0.06, 0.05, 0.55, 5).translate(0, 0.27, 0).rotateZ(-s * 1.0);
      const lo = new THREE.CylinderGeometry(0.05, 0.02, 0.75, 5).translate(0, -0.37, 0).rotateZ(-s * 0.25).translate(s * 0.46, 0.3, 0);
      parts.push({ g: paint(xf(up, [pv.x, pv.y, pv.z]), shade(base, 0.8), [0.7, 1]), bone: b });
      parts.push({ g: paint(xf(lo, [pv.x, pv.y, pv.z]), shade(base, 0.7), [0.5, 1]), bone: b });
    });
    height = 1.1;
  } else {
    // Wisp: a floating, softly glowing orb with a tapering trail.
    glow = true;
    pivots[CB.body]!.set(0, 1.4, 0);
    pivots[CB.head]!.set(0, 1.4, 0);
    pivots[CB.tail]!.set(0, 1.35, -0.3);
    parts.push({ g: paint(xf(new THREE.SphereGeometry(0.42, 14, 10), [0, 1.4, 0]), shade(base, 1.0), [0.9, 1.2]), bone: CB.body });
    parts.push({ g: paint(xf(new THREE.SphereGeometry(0.22, 10, 8), [0, 1.42, 0.25]), [1.1, 1.15, 1.1], [1, 1]), bone: CB.head });
    for (let i = 0; i < 4; i++) parts.push({ g: paint(xf(new THREE.SphereGeometry(0.26 - i * 0.05, 8, 6), [0, 1.33 - i * 0.12, -0.38 - i * 0.28]), shade(base, 0.85 - i * 0.1), [0.9, 1.1]), bone: CB.tail });
    eyes(parts, CB.head, 0.12, 1.47, 0.4, 0.05);
    height = 1.9;
  }

  // Skin attributes: each part rides one bone.
  const geos: THREE.BufferGeometry[] = [];
  for (const pt of parts) {
    const n = (pt.g.attributes['position'] as THREE.BufferAttribute).count;
    const si = new Uint16Array(n * 4);
    const sw = new Float32Array(n * 4);
    for (let i = 0; i < n; i++) {
      si[i * 4] = pt.bone;
      sw[i * 4] = 1;
    }
    pt.g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(si, 4));
    pt.g.setAttribute('skinWeight', new THREE.Float32BufferAttribute(sw, 4));
    geos.push(pt.g);
  }
  const geometry = mergeGeometries(geos, false)!;
  geos.forEach((g) => g.dispose());
  geometry.computeBoundingSphere();

  const bones: THREE.Bone[] = [];
  for (let i = 0; i < NB; i++) {
    const b = new THREE.Bone();
    b.name = `c${i}`;
    bones.push(b);
  }
  const rest: THREE.Vector3[] = [];
  for (let i = 0; i < NB; i++) {
    const par = parent[i]!;
    const lp = par < 0 ? pivots[i]!.clone() : pivots[i]!.clone().sub(pivots[par]!);
    bones[i]!.position.copy(lp);
    rest.push(lp.clone());
    if (par >= 0) bones[par]!.add(bones[i]!);
  }
  bones[0]!.updateMatrixWorld(true);
  return { kind, geometry, bones, rest, height, glow };
}

/**
 * Pose a creature rig for this frame (bones only; the view owns the root / death flop).
 * `move` is the frame's ground speed share (0..1); `t` seconds.
 */
/**
 * +ranger-specs: the spirit fox (light, big-eared, a huge white-tipped brush) and the great stag (long
 * legs, a deep chest and a crown of antlers), on the wolf's rig. Returns the height.
 */
function buildBeast(kind: 'fox' | 'stag', parts: Part[], pivots: THREE.Vector3[], base: [number, number, number]): number {
  const fox = kind === 'fox';
  const hip = fox ? 0.62 : 1.35;
  const half = fox ? 0.5 : 0.75;
  pivots[CB.body]!.set(0, hip + 0.1, 0);
  pivots[CB.head]!.set(0, hip + (fox ? 0.32 : 0.75), half + (fox ? 0.32 : 0.35));
  for (const [b, x, z] of [[CB.fl, 0.18, half], [CB.fr, -0.18, half], [CB.bl, 0.18, -half], [CB.br, -0.18, -half]] as const) pivots[b]!.set(fox ? x : x * 1.3, hip, z);
  pivots[CB.tail]!.set(0, hip + 0.18, -half - 0.35);
  if (fox) {
    parts.push({ g: paint(xf(ellipsoid(0.27, 0.27, 0.68), [0, hip + 0.16, -0.02]), base, [0.6, 1.15]), bone: CB.body });
    parts.push({ g: paint(xf(ellipsoid(0.26, 0.3, 0.26), [0, hip + 0.2, 0.42]), [1, 0.97, 0.92], [0.75, 1.1]), bone: CB.body }); // white chest
    parts.push({ g: paint(xf(ellipsoid(0.21, 0.2, 0.24), [0, hip + 0.42, 0.88]), base, [0.65, 1.1]), bone: CB.head });
    parts.push({ g: paint(xf(new THREE.ConeGeometry(0.11, 0.34, 8).rotateX(Math.PI / 2), [0, hip + 0.36, 1.18]), shade(base, 1.1), [0.7, 1.05]), bone: CB.head });
    parts.push({ g: paint(xf(new THREE.SphereGeometry(0.04, 6, 4), [0, hip + 0.37, 1.35]), 0x1a1a1a, [1, 1]), bone: CB.head });
    for (const s of [-1, 1]) {
      parts.push({ g: paint(xf(new THREE.ConeGeometry(0.1, 0.34, 4), [s * 0.12, hip + 0.68, 0.84], [-0.1, 0, -s * 0.25]), shade(base, 0.85), [0.8, 1]), bone: CB.head });
      parts.push({ g: paint(xf(new THREE.SphereGeometry(0.04, 6, 4), [s * 0.1, hip + 0.48, 1.06]), 0xfff2b0, [1.4, 1.4]), bone: CB.head });
    }
    for (const b of [CB.fl, CB.fr, CB.bl, CB.br]) {
      const pv = pivots[b]!;
      parts.push({ g: paint(xf(leg(hip, 0.065), [pv.x, pv.y, pv.z]), shade(base, 0.55), [0.45, 1]), bone: b });
    }
    // The brush: big, bushy, white-tipped.
    parts.push({ g: paint(xf(ellipsoid(0.2, 0.2, 0.55), [0, hip + 0.1, -half - 0.65], [0.5, 0, 0]), base, [0.7, 1.15]), bone: CB.tail });
    parts.push({ g: paint(xf(ellipsoid(0.15, 0.15, 0.2), [0, hip - 0.1, -half - 1.08], [0.5, 0, 0]), [1, 1, 0.97], [0.8, 1.2]), bone: CB.tail });
    return 1.25;
  }
  parts.push({ g: paint(xf(ellipsoid(0.42, 0.45, 0.95), [0, hip + 0.2, -0.05]), base, [0.6, 1.1]), bone: CB.body });
  parts.push({ g: paint(xf(ellipsoid(0.44, 0.55, 0.45), [0, hip + 0.3, 0.6]), shade(base, 1.15), [0.75, 1.1]), bone: CB.body });
  parts.push({ g: paint(xf(new THREE.CylinderGeometry(0.15, 0.22, 0.75, 8), [0, hip + 0.7, 0.85], [0.6, 0, 0]), base, [0.65, 1.05]), bone: CB.head }); // neck
  parts.push({ g: paint(xf(ellipsoid(0.2, 0.22, 0.38), [0, hip + 1.08, 1.18], [0.25, 0, 0]), base, [0.65, 1.1]), bone: CB.head });
  parts.push({ g: paint(xf(new THREE.SphereGeometry(0.06, 6, 4), [0, hip + 0.98, 1.55]), 0x1a1a1a, [1, 1]), bone: CB.head });
  for (const s of [-1, 1]) {
    parts.push({ g: paint(xf(new THREE.SphereGeometry(0.045, 6, 4), [s * 0.14, hip + 1.15, 1.32]), 0xfff2b0, [1.4, 1.4]), bone: CB.head });
    parts.push({ g: paint(xf(new THREE.ConeGeometry(0.08, 0.26, 4), [s * 0.2, hip + 1.27, 1.02], [0, 0, -s * 1.1]), shade(base, 0.85), [0.8, 1]), bone: CB.head });
    // Antlers: a main beam and three tines, glowing pale.
    const ant = [0.95, 1.0, 0.82] as const;
    parts.push({ g: paint(xf(new THREE.CylinderGeometry(0.035, 0.05, 0.85, 5), [s * 0.28, hip + 1.6, 1.0], [-0.35, 0, -s * 0.55]), ant, [1, 1.25]), bone: CB.head });
    for (let i = 0; i < 3; i++) parts.push({ g: paint(xf(new THREE.ConeGeometry(0.03, 0.34, 5), [s * (0.3 + i * 0.12), hip + 1.55 + i * 0.16, 1.05 - i * 0.12], [0.3, 0, -s * 0.15]), ant, [1, 1.25]), bone: CB.head });
  }
  for (const b of [CB.fl, CB.fr, CB.bl, CB.br]) {
    const pv = pivots[b]!;
    parts.push({ g: paint(xf(leg(hip, 0.09), [pv.x, pv.y, pv.z]), shade(base, 0.7), [0.45, 1]), bone: b });
  }
  parts.push({ g: paint(xf(ellipsoid(0.1, 0.16, 0.12), [0, hip + 0.3, -half - 0.4]), [1, 1, 0.97], [0.8, 1.15]), bone: CB.tail });
  return 2.55;
}

export function poseCreature(rig: CreatureRig, p: PoseParams, sq: { x: number; y: number }): void {
  const B = rig.bones;
  const R = rig.rest;
  for (let i = 0; i < B.length; i++) {
    B[i]!.position.copy(R[i]!);
    B[i]!.rotation.set(0, 0, 0);
    B[i]!.scale.set(1, 1, 1);
  }
  const run = p.run * (1 - p.stun) * (1 - p.dead);
  const s = Math.sin(p.runPhase);
  const c = Math.cos(p.runPhase);
  const atk = p.attack >= 0 ? Math.sin(Math.min(1, p.attack) * Math.PI) : 0;
  const breathe = Math.sin(p.t * 2.6);
  sq.x = 1;
  sq.y = 1;
  if (rig.kind === 'slime' || rig.kind === 'bloom') {
    // Hop cycle: up at mid-stride, squash on landing; idle wobble; lunge-stretch on attack.
    const hop = Math.abs(s) * run;
    const land = (1 - Math.abs(s)) * run;
    const wob = 0.05 * breathe * (1 - run);
    sq.y = 1 + wob + 0.18 * hop - 0.22 * land + 0.2 * atk - 0.25 * p.hit;
    sq.x = 1 / Math.sqrt(Math.max(0.3, sq.y));
    B[CB.body]!.position.y += hop * 0.55;
    B[CB.body]!.position.z += atk * 0.45;
    B[CB.body]!.rotation.x = 0.25 * atk - 0.15 * p.hit + 0.12 * run * c;
    B[CB.body]!.scale.set(sq.x, sq.y, sq.x);
    B[CB.head]!.rotation.y = p.twistHead * 0.5;
    B[CB.head]!.rotation.z = 0.08 * Math.sin(p.t * 1.7);
    return;
  }
  if (rig.kind === 'wisp') {
    const bob = Math.sin(p.t * 2.0) * 0.18;
    B[CB.body]!.position.y += bob + 0.1 * run;
    B[CB.body]!.position.z += atk * 0.3;
    B[CB.tail]!.rotation.y = Math.sin(p.t * 3.1) * 0.35;
    B[CB.tail]!.rotation.x = -0.2 * run;
    const pulse = 1 + 0.06 * Math.sin(p.t * 5) + 0.25 * (p.cast > 0 ? p.cast : 0) + 0.2 * atk;
    B[CB.body]!.scale.setScalar(pulse);
    return;
  }
  const legSwing = rig.kind === 'crawler' ? 0.45 : LUPINE.has(rig.kind) ? 0.85 : 0.7;
  const fl = s * legSwing * run;
  B[CB.fl]!.rotation.x = rig.kind === 'crawler' ? 0 : fl;
  B[CB.br]!.rotation.x = rig.kind === 'crawler' ? 0 : fl;
  B[CB.fr]!.rotation.x = rig.kind === 'crawler' ? 0 : -fl;
  B[CB.bl]!.rotation.x = rig.kind === 'crawler' ? 0 : -fl;
  if (rig.kind === 'crawler') {
    // Six legs scuttle sideways (yaw) in two tripods.
    B[CB.fl]!.rotation.y = fl;
    B[CB.mr]!.rotation.y = fl;
    B[CB.bl]!.rotation.y = fl;
    B[CB.fr]!.rotation.y = -fl;
    B[CB.ml]!.rotation.y = -fl;
    B[CB.br]!.rotation.y = -fl;
  }
  // Body bob + gallop pitch; breathing; attack: head lunges forward and down (a bite / gore).
  B[CB.body]!.position.y += Math.abs(c) * 0.1 * run + 0.015 * breathe;
  B[CB.body]!.rotation.x = -0.06 * c * run + 0.12 * atk - 0.1 * p.hit + 0.1 * p.stun;
  B[CB.body]!.position.z += atk * (rig.kind === 'boar' ? 0.5 : 0.35);
  B[CB.head]!.rotation.x = 0.35 * atk - 0.25 * p.hit + 0.05 * s * run + 0.3 * p.stun;
  B[CB.head]!.rotation.y = p.twistHead;
  B[CB.tail]!.rotation.y = Math.sin(p.t * (run > 0.3 ? 9 : 3)) * (LUPINE.has(rig.kind) ? 0.35 : 0.5);
  B[CB.tail]!.rotation.x = LUPINE.has(rig.kind) ? 0.25 * run - 0.2 * p.hit : 0;
}
